'use client'

import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { IconPlus, IconLoader2 } from '@tabler/icons-react'
import { createCollection } from '@/lib/collections'
import { useAuth } from '@/hooks/useAuth'

interface CreateCollectionDialogProps {
  trigger?: React.ReactNode
  onSuccess?: (collectionId: string) => void
}

export function CreateCollectionDialog({
  trigger,
  onSuccess
}: CreateCollectionDialogProps) {
  const t = useTranslations('collections')
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Not logged in')
      if (!name.trim()) throw new Error('Name is required')
      return createCollection(
        user.id,
        name.trim(),
        description.trim() || undefined
      )
    },
    onSuccess: (collection) => {
      toast.success(t('collectionCreated'))
      queryClient.invalidateQueries({ queryKey: ['all-collections'] })
      queryClient.invalidateQueries({ queryKey: ['user-collections'] })
      setOpen(false)
      setName('')
      setDescription('')
      onSuccess?.(collection.id)
    },
    onError: (error) => {
      if (error instanceof Error && error.message.includes('duplicate')) {
        toast.error(t('collectionNameExists'))
      } else {
        toast.error(t('collectionCreateError'))
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" className="gap-2">
            <IconPlus className="h-4 w-4" />
            Create deck
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create deck</DialogTitle>
            <DialogDescription>
              Create a deck for words, sentences, and custom cards.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Deck name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Travel phrases, Lesson 3, Business Chinese..."
                autoFocus
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending && (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
